import type {ReactElement} from 'react';
import {Text} from '@Components/text';
import type {TabItemContentStorage, TabKey} from '@Components/tabs';
import {Tabs} from '@Components/tabs';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import type {DesignSelectorTabSwitcherProps} from './design-selector-tab-switcher.types';
import styles from './design-selector-tab-switcher.module.scss';
import {openTeamsUpsellDialog, TeamsUpsellSource} from '@Libraries/teams-upsell.library';

export function DesignSelectorTabSwitcher({
  designSelectorTab,
  onTabSelected,
  isMobile,
  hideTabContainer = false,
  tabsId,
}: DesignSelectorTabSwitcherProps): ReactElement | null {
  if (hideTabContainer) {
    return null;
  }

  const isMyDesignsSelected = designSelectorTab === DesignSelectorTab.MY_DESIGNS;
  const isTeamSelected = designSelectorTab === DesignSelectorTab.SHARED_WITH_ME;
  const isDesignGallerySelected = designSelectorTab === DesignSelectorTab.GALLERY_TEMPLATES;

  const onSharedWithMeTabSelected = (): void => {
    if (window.PMW.isPAYGUser()) {
      openTeamsUpsellDialog(TeamsUpsellSource.DESIGN_SELECTOR_SHARED_TAB, {shouldOpenInNewTab: true});
    } else {
      onTabSelected(DesignSelectorTab.SHARED_WITH_ME);
    }
  };

  const onTabClick = (tabKey: TabKey): void => {
    if ((tabKey as DesignSelectorTab) === DesignSelectorTab.MY_DESIGNS) {
      onTabSelected(DesignSelectorTab.MY_DESIGNS);
    } else if ((tabKey as DesignSelectorTab) === DesignSelectorTab.GALLERY_TEMPLATES) {
      onTabSelected(DesignSelectorTab.GALLERY_TEMPLATES);
    } else {
      onSharedWithMeTabSelected();
    }
  };

  const getTabItems = (): TabItemContentStorage[] => {
    return [
      {tabKey: DesignSelectorTab.MY_DESIGNS, text: window.i18next.t('pmwjs_my_designs')},
      {tabKey: DesignSelectorTab.SHARED_WITH_ME, text: window.i18next.t('pmwjs_shared_with_me')},
      {tabKey: DesignSelectorTab.GALLERY_TEMPLATES, text: window.i18next.t('pmwjs_design_templates')},
    ];
  };

  const getDropdownText = (): string => {
    if (isMyDesignsSelected) {
      return window.i18next.t('pmwjs_my_designs');
    }
    if (isTeamSelected) {
      return window.i18next.t('pmwjs_team');
    }
    return window.i18next.t('pmwjs_design_gallery');
  };

  const getDesktopClasses = (): string => {
    return `${styles.upperContainerDesktop} ${isDesignGallerySelected ? 'spacing-m-b-0' : ''}`;
  };

  const getMobileClasses = (): string => {
    return `${styles.upperContainerMobile} ${isDesignGallerySelected ? 'spacing-m-b-0' : ''}`;
  };

  const getDesktopUpperContainer = (): ReactElement => {
    return (
      <div id="design-selector-onboarding-container" className={getDesktopClasses()}>
        <Tabs className="spacing-m-b-4" id={tabsId} tabItems={getTabItems()} selectedTab={designSelectorTab} onTabClick={onTabClick} />
      </div>
    );
  };

  const getMobileUpperContainer = (): ReactElement => {
    return (
      <div className={getMobileClasses()}>
        <Text val={window.i18next.t('pmwjs_select_a_design_lowercase')} className={`${styles.selectADesignTextMobile} _unmargin`} />
        <Dropdown
          className={styles.designSelectorMobileDropdown}
          selector={<DropdownDefaultSelectBox id="design-selector-dropdown-1" text={getDropdownText()} size={DROPDOWN_SIZE.SMALL} isLoading={false} />}
          popup={
            <div style={{width: '100%'}}>
              <ControlledList
                id="design-selector-dropdown-list"
                items={[
                  {
                    id: 'design-selector-dropdown-2',
                    text: window.i18next.t('pmwjs_my_designs'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    isSelected: isMyDesignsSelected,
                    onClick: (): void => {
                      onTabSelected(DesignSelectorTab.MY_DESIGNS);
                    },
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-3',
                    text: window.i18next.t('pmwjs_team'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: onSharedWithMeTabSelected,
                    showSelectionCheckmark: false,
                  },
                  {
                    id: 'design-selector-dropdown-4',
                    text: window.i18next.t('pmwjs_design_gallery'),
                    type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
                    onClick: (): void => {
                      onTabSelected(DesignSelectorTab.GALLERY_TEMPLATES);
                    },
                    showSelectionCheckmark: false,
                  },
                ]}
              />
            </div>
          }
        />
      </div>
    );
  };

  return isMobile ? getMobileUpperContainer() : getDesktopUpperContainer();
}
